import { useState } from 'react';
import { BottomSheet } from '../common/BottomSheet';
import { ACTIVITY_META, ACTIVITY_TYPES, type ActivityType } from '../../types/activity';
import type { Plant } from '../../types/plant';

interface ActivityLogSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plant: Plant;
  onLog: (types: ActivityType[], note: string | null) => Promise<void>;
}

// Exclude 'watered' (has its own FAB) and 'note' (shown separately)
const CARE_TYPES = ACTIVITY_TYPES.filter((t) => t !== 'watered' && t !== 'note');

export function ActivityLogSheet({ open, onOpenChange, plant, onLog }: ActivityLogSheetProps) {
  const [selected, setSelected] = useState<Set<ActivityType>>(new Set());
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const displayName = plant.nickname || plant.commonName;

  const toggleType = (type: ActivityType) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const handleLog = async () => {
    const types = Array.from(selected);
    // If only note is provided with no care types, log as 'note' type
    if (types.length === 0 && noteOpen && note.trim()) {
      setSaving(true);
      await onLog(['note'], note.trim());
      reset();
      return;
    }
    if (types.length === 0) return;
    setSaving(true);
    await onLog(types, note.trim() || null);
    reset();
  };

  const reset = () => {
    setSaving(false);
    setSelected(new Set());
    setNote('');
    setNoteOpen(false);
    onOpenChange(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelected(new Set());
      setNote('');
      setNoteOpen(false);
    }
    onOpenChange(isOpen);
  };

  const hasSelection = selected.size > 0 || (noteOpen && note.trim().length > 0);

  return (
    <BottomSheet
      open={open}
      onOpenChange={handleOpenChange}
      title="Log Activity"
      description={`What did you do for ${displayName}?`}
      initialSnap="half"
    >
      <div className="space-y-5">
        {/* Activity type buttons — multi-select */}
        <div className="grid grid-cols-3 gap-3">
          {CARE_TYPES.map((type) => {
            const meta = ACTIVITY_META[type];
            const isSelected = selected.has(type);
            return (
              <button
                key={type}
                type="button"
                onClick={() => toggleType(type)}
                className={`flex flex-col items-center gap-2 p-4 rounded-lg border transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                aria-label={meta.label}
                aria-pressed={isSelected}
              >
                <span
                  className={`material-symbols-outlined text-2xl ${isSelected ? 'text-primary' : meta.color}`}
                  aria-hidden="true"
                >
                  {meta.icon}
                </span>
                <span className="font-sans text-xs font-medium text-editorial-black">
                  {meta.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Add Note toggle */}
        {!noteOpen ? (
          <button
            type="button"
            onClick={() => setNoteOpen(true)}
            className="flex items-center gap-2 w-full p-3 rounded-lg border border-gray-200 bg-white hover:border-gray-300 transition-colors"
            aria-label="Add a note"
          >
            <span className="material-symbols-outlined text-lg text-gray-400" aria-hidden="true">
              edit_note
            </span>
            <span className="font-sans text-sm text-gray-500">Add a note...</span>
          </button>
        ) : (
          <div>
            <label htmlFor="activity-note" className="block font-sans text-xs font-medium text-gray-500 uppercase tracking-widest mb-2">
              Note
            </label>
            <textarea
              id="activity-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Fresh potting mix, moved to brighter spot"
              rows={2}
              maxLength={500}
              autoFocus
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 font-sans text-sm text-editorial-black placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none"
            />
          </div>
        )}

        {/* Log button */}
        <button
          type="button"
          onClick={handleLog}
          disabled={!hasSelection || saving}
          className="w-full py-3 rounded-lg bg-primary text-white font-sans font-bold text-sm uppercase tracking-widest disabled:opacity-40 transition-opacity"
          aria-label="Save activity"
        >
          {saving
            ? 'Saving...'
            : selected.size > 1
              ? `Log ${selected.size} Activities`
              : 'Log Activity'}
        </button>
      </div>
    </BottomSheet>
  );
}
